import { getAllStoreItems, getDbMetaDataForSync } from 'src/api/offline/offlineApi';
import { OfflineConstants } from 'src/api/offline/constants';
import { useAppStore } from 'src/context/AppContext';
import useSyncOfflineAolItems from './useSyncOfflineAolItems';
import useSyncOfflineReportEvents from './useSyncOfflineReportEvents';
import useSyncOfflineBunkering from './useSyncOfflineBunkering';
import useSyncOfflineCargoTempLog from './useSyncOfflineCargoTempLog';
import useSyncOfflineStowageData from './useSyncOfflineStowageData';
import useSyncOfflineErrorLog from './useSyncOfflineErrorLog';

let isSyncing = false;
let consecutiveFailures = 0;
let backoffUntil = 0;
const MAX_CONSECUTIVE_FAILURES = 3;
const BACKOFF_BASE_MS = 300_000; // 5 minutes
const BACKOFF_MAX_MS = 3_600_000; // 1 hour cap
const ERROR_LOG_STORE_NAME = OfflineConstants.stores.errorLog.storeName;

const useSyncOfflineData = () => {
    const { syncReportEvents } = useSyncOfflineReportEvents();
    const { syncAolItems } = useSyncOfflineAolItems();
    const { syncBunkerings } = useSyncOfflineBunkering();
    const { syncOfflineCargoTempLog } = useSyncOfflineCargoTempLog();
    const { syncStowagePlanData } = useSyncOfflineStowageData();
    const { syncErrorLog } = useSyncOfflineErrorLog();
    const { setAppModalData } = useAppStore();

    const syncOfflineData = async () => {
        if (isSyncing) return;

        // Backoff: if we've failed too many times, skip until cooldown expires
        if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES && Date.now() < backoffUntil) {
            return;
        }

        await navigator.locks.request('orcaweb-offline-sync', { ifAvailable: true }, async (lock) => {
            if (!lock) return;

            isSyncing = true;
            try {
                const dbMetaData = await getDbMetaDataForSync();
                const errorLogItems = await getAllStoreItems(ERROR_LOG_STORE_NAME);
                const hasErrorLogItems = errorLogItems.length > 0;

                if (dbMetaData.itemCount === 0 && !hasErrorLogItems) {
                    consecutiveFailures = 0;
                    backoffUntil = 0;
                    return;
                }

                if (dbMetaData.itemCount > 0) {
                    setAppModalData({ title: 'Offline sync', message: 'You have been offline for a while. Please wait while we sync your offline data' });
                }

                let syncFailed = false;

                try {
                    if (hasErrorLogItems) {
                        await syncErrorLog();
                    }
                    if (dbMetaData.storeCounts.reportEventsStore > 0) {
                        await syncReportEvents();
                    }
                    if (dbMetaData.storeCounts.aolStore > 0) {
                        await syncAolItems();
                    }
                    if (dbMetaData.storeCounts.bunkeringStore > 0) {
                        await syncBunkerings();
                    }
                    if (dbMetaData.storeCounts.cargoTempLogStore > 0) {
                        await syncOfflineCargoTempLog();
                    }
                    if (dbMetaData.storeCounts.stowageStore > 0) {
                        await syncStowagePlanData();
                    }
                } catch {
                    syncFailed = true;
                }

                // Re-check IndexedDB: individual handlers may have failed silently
                const postSyncMeta = await getDbMetaDataForSync();
                const remainingErrorLogItems = await getAllStoreItems(ERROR_LOG_STORE_NAME);
                if (syncFailed || postSyncMeta.itemCount > 0 || remainingErrorLogItems.length > 0) {
                    consecutiveFailures++;
                    if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
                        // Exponential backoff: 5min, 15min, 45min, capped at 1 hour
                        const backoffMs = Math.min(
                            BACKOFF_BASE_MS * Math.pow(3, consecutiveFailures - MAX_CONSECUTIVE_FAILURES),
                            BACKOFF_MAX_MS
                        );
                        backoffUntil = Date.now() + backoffMs;
                    }
                } else {
                    consecutiveFailures = 0;
                    backoffUntil = 0;
                }
            } finally {
                isSyncing = false;
                setAppModalData(null);
            }
        });
    };

    return {
        syncOfflineData,
        resetSyncBackoff: () => { consecutiveFailures = 0; backoffUntil = 0; },
    };
};

export default useSyncOfflineData;
